import { useEffect, useRef, useState } from "react";

import { ArrowPathIcon, HashtagIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import useLocations from "../../data/useLocations";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import { LeadSource } from "../../models/Lead";
import { VehicleComplexity } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { complexityLabel } from "../../utils/vehicleDisplay";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { VehicleDetailsInput } from "../parts/VehicleDetailsInput";
import { YearCombobox } from "../parts/YearCombobox";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import {
  BODY_STYLE_OPTIONS,
  DOORS_OPTIONS,
  ENGINE_SIZE_OPTIONS,
  FilterStaticCombobox,
  FUEL_OPTIONS,
  TRANSMISSION_OPTIONS,
} from "../stock/VehiclePartSearchFilters";

// Vehicle-detail filters that can be toggled on/off (mirrors the parts panel pattern).
type VehicleDetailFilterKey =
  | "yearFrom"
  | "yearTo"
  | "bodyStyle"
  | "numberOfDoors"
  | "fuelType"
  | "engineSize"
  | "engineCode"
  | "transmissionType";

const defaultVehicleDetailFilterEnabled: Record<VehicleDetailFilterKey, boolean> = {
  yearFrom: true,
  yearTo: true,
  bodyStyle: true,
  numberOfDoors: true,
  fuelType: true,
  engineSize: true,
  engineCode: true,
  transmissionType: true,
};

const STORAGE_KEY = "wilma:vehicleSearch:detailFilters";

interface IVehicleSearchFields {
  vrm: string;
  make: string;
  model: string;
  yearFrom: string;
  yearTo: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  stockNumber: string;
  source: string;
}

const defaultValues: IVehicleSearchFields = {
  vrm: "",
  make: "",
  model: "",
  yearFrom: "",
  yearTo: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  stockNumber: "",
  source: "",
};

function readSavedDetailFilterEnabled(): Record<VehicleDetailFilterKey, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultVehicleDetailFilterEnabled;
    const parsed = JSON.parse(saved) as Partial<Record<VehicleDetailFilterKey, unknown>>;
    return Object.keys(defaultVehicleDetailFilterEnabled).reduce((result, key) => {
      const filterKey = key as VehicleDetailFilterKey;
      result[filterKey] = typeof parsed[filterKey] === "boolean" ? (parsed[filterKey] as boolean) : defaultVehicleDetailFilterEnabled[filterKey];
      return result;
    }, { ...defaultVehicleDetailFilterEnabled });
  } catch {
    return defaultVehicleDetailFilterEnabled;
  }
}

function saveDetailFilterEnabled(enabled: Record<VehicleDetailFilterKey, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
  } catch {
    // Ignore storage failures; filters still work for the current session.
  }
}

export function buildVehicleSearchBody(
  data: IVehicleSearchFields,
  enabled: Record<VehicleDetailFilterKey, boolean>,
  locationId: string,
  complexity: string
): Record<string, any> {
  return {
    vrm: data.vrm ? data.vrm.trim() : undefined,
    make: data.make || undefined,
    model: data.model || undefined,
    stockNumber: data.stockNumber || undefined,
    source: data.source !== "" ? Number(data.source) : undefined,
    yearFrom: enabled.yearFrom && data.yearFrom ? Number(data.yearFrom) : undefined,
    yearTo: enabled.yearTo && data.yearTo ? Number(data.yearTo) : undefined,
    bodyStyle: enabled.bodyStyle && data.bodyStyle ? data.bodyStyle : undefined,
    numberOfDoors: enabled.numberOfDoors && data.numberOfDoors ? Number(data.numberOfDoors) : undefined,
    fuelType: enabled.fuelType && data.fuelType ? data.fuelType : undefined,
    transmissionType: enabled.transmissionType && data.transmissionType ? data.transmissionType : undefined,
    engineSize: enabled.engineSize && data.engineSize ? data.engineSize : undefined,
    engineCode: enabled.engineCode && data.engineCode ? data.engineCode : undefined,
    locationId: locationId || undefined,
    complexity: complexity !== "" ? Number(complexity) : undefined,
  };
}

const sourceOptions = Object.values(LeadSource)
  .filter((value): value is number => typeof value === "number" && value !== LeadSource.All)
  .map((value) => ({ value, label: LeadSource[value] }));

const complexityOptions = [
  VehicleComplexity.Unranked,
  VehicleComplexity.Easy,
  VehicleComplexity.Medium,
  VehicleComplexity.Hard,
].map((value) => ({ value, label: complexityLabel(value) }));

interface VehicleSearchFiltersProps {
  onApply: (body: Record<string, any>) => void;
  onClear: () => void;
  disabled?: boolean;
}

export default function VehicleSearchFilters({ onApply, onClear, disabled = false }: VehicleSearchFiltersProps) {
  const { vrmLookup } = useVehiclesMutations();
  const { locations } = useLocations();
  const { register, setValue, getValues, watch, reset } = useForm<IVehicleSearchFields>({ defaultValues });

  const [detailFilterEnabled, setDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);
  const [locationId, setLocationId] = useState("");
  const [complexity, setComplexity] = useState("");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const vehicleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchYearFrom = watch("yearFrom");
  const watchYearTo = watch("yearTo");
  const watchBodyStyle = watch("bodyStyle");
  const watchNumberOfDoors = watch("numberOfDoors");
  const watchFuelType = watch("fuelType");
  const watchEngineSize = watch("engineSize");
  const watchEngineCode = watch("engineCode");
  const watchTransmission = watch("transmissionType");

  const locationOptions = [{ id: "", name: "Any location" }, ...(locations.data?.data ?? [])];

  useEffect(() => {
    setDetailFilterEnabled(readSavedDetailFilterEnabled());
  }, []);

  useEffect(() => {
    return () => {
      if (vehicleTimeoutRef.current) clearTimeout(vehicleTimeoutRef.current);
    };
  }, []);

  const setDetailFilterEnabledValue = (key: VehicleDetailFilterKey, value: boolean) => {
    setDetailFilterEnabled((current) => {
      const next = { ...current, [key]: value };
      saveDetailFilterEnabled(next);
      return next;
    });
  };

  const handleGetVehicle = () => {
    const vrm = watch("vrm");
    if (!vrm || fetchLoading || disabled) return;
    if (vehicleTimeoutRef.current) clearTimeout(vehicleTimeoutRef.current);
    setFetchLoading(true);
    vrmLookup.mutate(vrm, {
      onSuccess: (data) => {
        const vehicle = data.data;
        if (!vehicle) return;
        setValue("make", (vehicle.combinedMake ?? "").trim());
        setValue("model", (vehicle.combinedModel || vehicle.modelRangeDescription || "").trim());
        setValue("bodyStyle", vehicle.bodyStyle ?? "");
        setValue("numberOfDoors", vehicle.numberOfDoors?.toString() ?? "");
        setValue("fuelType", vehicle.combinedFuelType ?? "");
        setValue("transmissionType", vehicle.combinedTransmission ?? "");
        setValue("engineCode", vehicle.engineModelCode ?? "");
        const yearFrom = vehicle.visibilityDate && DateTime.fromISO(vehicle.visibilityDate).year;
        const yearTo = vehicle.terminateDate && DateTime.fromISO(vehicle.terminateDate).year;
        setValue("yearFrom", yearFrom?.toString() ?? "");
        setValue("yearTo", yearTo?.toString() ?? "");
        setValue("engineSize", vehicle.engineCapacity ? (Math.round(vehicle.engineCapacity / 100) * 100 / 1000).toString() : "");
      },
      onSettled: () => {
        vehicleTimeoutRef.current = setTimeout(() => setFetchLoading(false), 500);
      },
    });
  };

  const handleSearch = () => {
    if (disabled) return;
    onApply(buildVehicleSearchBody(getValues(), detailFilterEnabled, locationId, complexity));
  };

  const handleClear = () => {
    reset(defaultValues);
    setLocationId("");
    setComplexity("");
    setResetKey((k) => k + 1);
    onClear();
  };

  const isLookupDisabled = disabled || fetchLoading;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900">Search Vehicles</h3>

      <div className="mt-3 grid grid-cols-1 items-end gap-3 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <label className="text-sm font-medium text-black">Search Vehicles</label>
          <div className="mt-1 flex items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm focus-within:ring-2 focus-within:ring-primary-500">
            <span aria-hidden="true" className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300">UK</span>
            <input
              {...register("vrm")}
              disabled={fetchLoading}
              placeholder="ENTER VRM / VIN"
              aria-label="VRM or VIN"
              className="w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-sm placeholder:tracking-[0.15em] placeholder:text-black/40 focus:outline-none focus:ring-0 disabled:opacity-60"
              style={{ fontFamily: '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif' }}
            />
            <button
              type="button"
              onClick={handleGetVehicle}
              disabled={isLookupDisabled}
              title={fetchLoading ? "Fetching..." : "Fetch vehicle details"}
              className={classNames("flex w-9 items-center justify-center bg-black text-yellow-300 transition-colors", isLookupDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-800")}
            >
              {fetchLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" /> : <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div className="lg:col-span-4">
          <label className="text-sm font-medium text-wilma-700">Stock Number</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500"><HashtagIcon className="h-4 w-4" aria-hidden="true" /></span>
            <input {...register("stockNumber")} type="text" placeholder="Stock..." disabled={disabled} className="w-full rounded-md border border-wilma-400 bg-wilma-50 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:font-normal placeholder:text-wilma-400 focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500 disabled:bg-gray-100" />
          </div>
        </div>

        <div className="lg:col-span-4">
          <label className="text-sm font-medium text-wilma-700">Source</label>
          <select
            {...register("source")}
            disabled={disabled}
            className="mt-1 w-full rounded-md border border-wilma-400 bg-wilma-50 px-3 py-2 text-sm text-wilma-900 focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500 disabled:bg-gray-100"
          >
            <option value="">Any source</option>
            {sourceOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-2 [&>div]:max-w-[10rem] flex flex-wrap gap-x-2 gap-y-1">
        <div><MakeCombobox label="Make" onMakeSelect={(make) => { setValue("make", make?.make ?? ""); setValue("model", ""); }} currentValue={watchMake} /></div>
        <div><ModelCombobox label="Model" make={watchMake} onModelSelect={(model) => setValue("model", model?.range ?? "")} currentValue={watchModel} /></div>
        <div className="!max-w-[6rem]"><YearCombobox label="Year From" currentValue={watchYearFrom} maxYear={detailFilterEnabled.yearTo && watchYearTo ? Number(watchYearTo) : undefined} onYearSelect={(year) => setValue("yearFrom", year ?? "")} placeholder="From..." disabled={disabled} filterEnabled={detailFilterEnabled.yearFrom} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("yearFrom", enabled)} /></div>
        <div className="!max-w-[6rem]"><YearCombobox label="Year To" currentValue={watchYearTo} minYear={detailFilterEnabled.yearFrom && watchYearFrom ? Number(watchYearFrom) : undefined} onYearSelect={(year) => setValue("yearTo", year ?? "")} placeholder="To..." disabled={disabled} filterEnabled={detailFilterEnabled.yearTo} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("yearTo", enabled)} /></div>
        <div><FilterStaticCombobox label="Body Style" value={watchBodyStyle} onChange={(value) => setValue("bodyStyle", value)} options={BODY_STYLE_OPTIONS} disabled={disabled} placeholder="Any" filterEnabled={detailFilterEnabled.bodyStyle} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("bodyStyle", enabled)} /></div>
        <div><FilterStaticCombobox label="Doors" value={watchNumberOfDoors} onChange={(value) => setValue("numberOfDoors", value)} options={DOORS_OPTIONS} disabled={disabled} placeholder="Any" filterEnabled={detailFilterEnabled.numberOfDoors} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("numberOfDoors", enabled)} /></div>
        <div><FilterStaticCombobox label="Fuel" value={watchFuelType} onChange={(value) => setValue("fuelType", value)} options={FUEL_OPTIONS} disabled={disabled} placeholder="Any" filterEnabled={detailFilterEnabled.fuelType} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("fuelType", enabled)} /></div>
        <div><FilterStaticCombobox label="Engine Size" value={watchEngineSize} onChange={(value) => setValue("engineSize", value)} options={ENGINE_SIZE_OPTIONS} disabled={disabled} placeholder="Any" filterEnabled={detailFilterEnabled.engineSize} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("engineSize", enabled)} /></div>
        <div><VehicleDetailsInput label="Engine Code" onSelect={(value) => setValue("engineCode", value ?? "")} currentValue={watchEngineCode} filterEnabled={detailFilterEnabled.engineCode} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("engineCode", enabled)} disabled={disabled} /></div>
        <div><FilterStaticCombobox label="Transmission" value={watchTransmission} onChange={(value) => setValue("transmissionType", value)} options={TRANSMISSION_OPTIONS} disabled={disabled} placeholder="Any" filterEnabled={detailFilterEnabled.transmissionType} onFilterEnabledChange={(enabled) => setDetailFilterEnabledValue("transmissionType", enabled)} /></div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-black">Yard Location</label>
          <ComboBoxInput
            key={`vehicle-search-location-${resetKey}`}
            options={locationOptions}
            searchField="name"
            valueField="id"
            registerName="locationId"
            register={null}
            setValue={(_name: string, value: string) => setLocationId(value || "")}
            initialValue={locationId}
            placeholder="Any location"
            containerClasses="w-full"
            disabled={disabled || locations.isLoading}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-black">Complexity</label>
          <select
            value={complexity}
            onChange={(e) => setComplexity(e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-md border border-gray-400 px-3 py-2 text-sm disabled:bg-gray-100"
          >
            <option value="">Any complexity</option>
            {complexityOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-x-4 pt-1">
        <ButtonInput
          label="Search"
          onClick={handleSearch}
          classes={classNames(disabled ? "" : "!bg-primary-600 hover:!bg-primary-700")}
          disabled={disabled}
        />
        <ButtonInput
          label="Clear"
          classes={classNames(disabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
          onClick={handleClear}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
